import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, Checkbox, CircularProgress, Typography, makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import DialogGrid from 'components/_dialogs/_components/DialogGrid';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import type { Member, OrganizationRole, RoleCatalogItem } from 'config/api/members/_types';
import members from 'config/api/members/members';
import usersEndpoints from 'config/api/users/users';
import useApiCall from 'hooks/useApiCall';
import general_messages from 'messages/general_messages';
import table_messages from 'messages/table_messages';
import title_messages from 'messages/title_messages';
import user_messages from 'messages/user_messages';

const CATEGORY_ORDER = ['general', 'tools_functions', 'dashboards', 'administration'];

const useStyles = makeStyles(theme => ({
  diffSummary: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  addedRoles: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  removedRoles: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  member: Member | null;
  refreshData: () => void;
}

const fetchRoleCatalog = async (): Promise<RoleCatalogItem[]> => {
  // Using native fetch or axios directly is fine, but since useApiCall is widespread,
  // we can use standard axios (or fetch) here as long as it gets the token.
  // Actually, standard react-query implementations here use axios.
  const { default: axios } = await import('axios');
  const response = await axios(members.getRoleCatalog());
  return response.data;
};

const RoleAssignmentDialog: React.FC<Props> = ({ open, onClose, member, refreshData }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { apiCall } = useApiCall();

  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);

  const {
    data: catalog = [],
    isLoading: catalogLoading,
    isError: catalogError,
  } = useQuery<RoleCatalogItem[]>(['ROLE_CATALOG'], fetchRoleCatalog, { enabled: open });

  const initialRoleIds = useMemo(() => member?.organization_roles.map(r => r.role) || [], [member]);

  useEffect(() => {
    if (open && member) {
      setSelectedRoles(initialRoleIds);
    }
  }, [open, member, initialRoleIds]);

  const handleToggle = (roleValue: number) => {
    setSelectedRoles(prev => (prev.includes(roleValue) ? prev.filter(v => v !== roleValue) : [...prev, roleValue]));
  };

  const getRoleLabel = (value: number) => {
    const role = catalog.flatMap(c => c.roles).find(r => r.value === value);
    return role?.label ?? `Role ${value}`;
  };

  const addedRoles = selectedRoles.filter(r => !initialRoleIds.includes(r));
  const removedRoles = initialRoleIds.filter(r => !selectedRoles.includes(r));

  const saveMutation = useMutation(
    async () => {
      if (!member) return;
      const organizationId = member.organization.id;
      const organizationRoles: OrganizationRole[] = selectedRoles.map(role => ({
        role,
        organization_id: organizationId,
      }));

      const payload = {
        profile: member.profile,
        organization_roles: organizationRoles,
      };

      const response = await apiCall(usersEndpoints.update(member.id), { data: payload });
      if (response && response.status >= 400) {
        const message =
          typeof response.data?.organization_roles === 'string'
            ? response.data.organization_roles
            : response.data?.detail ?? 'Failed to update roles';
        const err = new Error(message) as Error & { status?: number; responseData?: unknown };
        err.status = response.status;
        err.responseData = response.data;
        throw err;
      }
    },
    {
      onSuccess: () => {
        enqueueSnackbar(t(general_messages.success_updated[0], general_messages.success_updated[1]), { variant: 'success' });
        refreshData();
        onClose();
      },
      onError: (error: (Error & { status?: number }) | null) => {
        const status = error?.status;
        const msg =
          status === 403
            ? t(user_messages.role_assignment_403[0], user_messages.role_assignment_403[1])
            : error?.message || t(general_messages.something_went_wrong[0], general_messages.something_went_wrong[1]);
        enqueueSnackbar(msg, { variant: 'error' });
        if (status !== 403) {
          enqueueSnackbar(t(user_messages.role_assignment_error_retry[0], user_messages.role_assignment_error_retry[1]), {
            variant: 'info',
          });
        }
      },
    },
  );

  const sortedCategories = useMemo(() => {
    if (!catalog.length) return [];
    return [...catalog].sort((a, b) => {
      const indexA = CATEGORY_ORDER.indexOf(a.category);
      const indexB = CATEGORY_ORDER.indexOf(b.category);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
  }, [catalog]);

  if (!member) return null;

  return (
    <DialogGrid
      color='secondary'
      dialogActions={
        <CenteredGrid gridGap={2} width='sm' withoutPadding>
          {saveMutation.isError && (
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={saveMutation.isLoading}
              icon={false}
              onClick={() => saveMutation.mutate()}
              showLoader={saveMutation.isLoading}
              textLike={false}
              variant='outlined'
            >
              {t(general_messages.retry[0], general_messages.retry[1])}
            </ColoredButton>
          )}
          <ColoredButton
            className=''
            customColor='secondary'
            disabled={saveMutation.isLoading || selectedRoles.length === 0}
            icon={false}
            onClick={() => saveMutation.mutate()}
            showLoader={saveMutation.isLoading}
            textLike={false}
            variant='outlined'
          >
            {t(general_messages.save[0], general_messages.save[1])}
          </ColoredButton>
          <Button disabled={saveMutation.isLoading} onClick={onClose} size='small'>
            {t(general_messages.cancel[0], general_messages.cancel[1])}
          </Button>
        </CenteredGrid>
      }
      displayTitle
      fullWidth
      loading={false}
      loadingCover={false}
      noPadding={false}
      onClose={onClose}
      open={open}
      scrollDisabled={false}
      sending={saveMutation.isLoading}
      title={`${t(title_messages.edit_coworker[0], title_messages.edit_coworker[1])} - ${member.profile.first_name} ${
        member.profile.last_name
      }`}
      withCloseConfirmation={false}
    >
      <Box p={2}>
        {saveMutation.isError && (
          <Typography color='error' paragraph variant='body2'>
            {t(user_messages.role_assignment_error_retry[0], user_messages.role_assignment_error_retry[1])}
          </Typography>
        )}
        {catalogLoading && (
          <Box display='flex' justifyContent='center' p={4}>
            <CircularProgress />
          </Box>
        )}
        {!catalogLoading && catalogError && (
          <Typography color='error'>{t(general_messages.something_went_wrong[0], general_messages.something_went_wrong[1])}</Typography>
        )}
        {!catalogLoading && !catalogError && (
          <>
            {sortedCategories.map(category => (
              <Box key={category.category} mb={3}>
                <Typography gutterBottom variant='h6'>
                  {category.label}
                </Typography>
                <CenteredGrid withoutPadding>
                  {category.roles.map(role => (
                    <div key={role.value} className={classes.checkboxItem}>
                      <Checkbox
                        checked={selectedRoles.includes(role.value)}
                        color='primary'
                        disabled={saveMutation.isLoading}
                        onChange={() => handleToggle(role.value)}
                      />
                      <Typography>{role.label}</Typography>
                    </div>
                  ))}
                </CenteredGrid>
              </Box>
            ))}

            {(addedRoles.length > 0 || removedRoles.length > 0) && (
              <Box className={classes.diffSummary}>
                <Typography gutterBottom variant='subtitle1'>
                  {t(table_messages.summary_changes[0], table_messages.summary_changes[1])}
                </Typography>
                {addedRoles.length > 0 && (
                  <Typography className={classes.addedRoles}>+ Added: {addedRoles.map(getRoleLabel).join(', ')}</Typography>
                )}
                {removedRoles.length > 0 && (
                  <Typography className={classes.removedRoles}>- Removed: {removedRoles.map(getRoleLabel).join(', ')}</Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </DialogGrid>
  );
};

export default RoleAssignmentDialog;
